import { uniqBy } from "lodash";

import type { ISite } from "@similarweb/ui-components";

import { BLOCK_SITE_STATUS } from "pro-features/components/AutocompleteDigitalMarketing/AutocompleteDigitalMarketingHelper";
import type { IChosenItem, IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { getQueryBarSecondaryTextMap } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { autocompleteStates } from "app/components/compare/WebsiteQueryBarTypes";
import { translate } from "app/filters/i18nFilter";
import { fetchSiteInfoQuery } from "app/services/sitesService/queries/sitesQueries";
import type { IWebsiteRecent as IWebsiteRecentBase } from "app/services/solutions2Services/HomepageDataFetchers/HomepageDataFetcherTypes";

import type { IShowExtendedTypeResults, IWebsiteItem, IWebsitePageResult, WebsitePageSearchType } from "../types";
import { getUrlComponents, getDomainWithSubdomain, removeTrailingSlash } from "../urlComponentUtils";

type IWebsiteRecent = IWebsiteRecentBase & {
    data: {
        params: {
            /** page = "site1,site2,site3" */
            pageFilter: string;
        };
    };
};

export const filterOutIllegalWebsiteQueryBarItems = (result: ISite[]) => {
    if (result.length === 1 && !getUrlComponents(result[0].name).domain) {
        return [autocompleteStates.NO_DATA_SEARCHES];
    } else {
        return result.filter((item) => {
            const urlObj = getUrlComponents(item.name);
            return !!urlObj.domain;
        });
    }
};

export const filterOutIllegalWebsiteItems = (item: ISite) => {
    const urlObj = getUrlComponents(item.name);
    return !!urlObj.domain;
};

const normalizedPageLevelRecent = (item: IWebsiteRecent, showCompareSites = false) => {
    const itemData = item.data;
    const pageLevelData: IChosenItemParams = itemData.params?.pageFilter ? JSON.parse(itemData.params.pageFilter) : [];

    if (showCompareSites && (pageLevelData.length > 1 || itemData.comparedItems?.length)) {
        let compareItems = [];
        if (pageLevelData.length > 1) {
            compareItems = pageLevelData.slice(1).map((item) => {
                const itemComponents = getUrlComponents(item.url);
                const domainAndSubdomain = getDomainWithSubdomain(
                    itemComponents.domain ?? "",
                    itemComponents.subdomain ?? "",
                );
                const compareSiteIndex = [itemData.mainItem, ...itemData.comparedItems].findIndex(
                    (comparedItem) => domainAndSubdomain === comparedItem,
                );

                return {
                    domain: domainAndSubdomain,
                    fullUrl: item.url,
                    searchType: item.searchType,
                    iconUrl: [
                        itemData.resources.mainItemFavIcon,
                        ...(itemData.resources.comparedItemsFavicon as unknown as string[]),
                    ][compareSiteIndex],
                };
            });
        } else {
            compareItems = itemData.comparedItems.map((comparedItem, index) => {
                const { subdomain } = getUrlComponents(comparedItem);
                return {
                    domain: comparedItem,
                    fullUrl: comparedItem,
                    searchType: subdomain ? "subdomain" : "domain",
                    iconUrl: itemData.resources.comparedItemsFavicon[index],
                };
            });
        }
        return {
            mainSite: {
                domain: itemData.mainItem,
                fullUrl: pageLevelData[0]?.url,
                searchType: pageLevelData[0]?.searchType,
                iconUrl: itemData.resources.mainItemFavIcon,
            },
            compareItems,
        };
    } else {
        const query = pageLevelData[0]?.url ?? itemData.mainItem;
        const secondaryText = getCustomIndicator(
            pageLevelData[0]?.searchType,
            pageLevelData[0]?.searchType ? query : undefined,
        )(query);
        return {
            name: query,
            image: itemData.resources.mainItemFavIcon,
            icon: itemData.resources.mainItemFavIcon,
            searchType: pageLevelData[0]?.searchType,
            fullUrl: pageLevelData[0]?.url,
            secondaryText,
        };
    }
};

export const renderItemCreator =
    <T>(type: string, onClick: (item: T, type: string) => void) =>
    (item: T) => {
        return { ...item, type, props: { onClick: onClick(item, type) } };
    };

export const getCustomIndicator = (searchType?: WebsitePageSearchType, defaultValue?: string) => (query?: string) => {
    if (!query) {
        return;
    }
    const { domain, subdomain, folder, queryParams } = getUrlComponents(query);

    const normalizedQuery =
        constructQueryWithNormalizedDomainAndSubdomain(domain ?? "", subdomain ?? "", folder, queryParams) ?? query;

    const queryBarSecondaryTextMap = getQueryBarSecondaryTextMap();
    const shouldUseDefaultSearchType = normalizedQuery === defaultValue;
    if (shouldUseDefaultSearchType && searchType) {
        return queryBarSecondaryTextMap[searchType];
    }

    if (queryParams?.length) {
        return queryBarSecondaryTextMap.exact;
    }
    if (folder?.length) {
        return queryBarSecondaryTextMap.folder;
    }
    if (subdomain?.length) {
        return queryBarSecondaryTextMap.subdomain;
    }
    return queryBarSecondaryTextMap.domain;
};

export const getPageLevelAutoCompleteItems = async (
    onClick: FixmeType,
    query: string,
    domain?: string,
    subdomain?: string,
    folder?: string,
    queryParams?: string,
    extraItems: IWebsitePageResult[] = [],
    excludes: IChosenItem[] = [],
    originalQuery?: string,
    addCustomIndicator?: IShowExtendedTypeResults,
) => {
    const partialOnClickFunction = (item: IWebsitePageResult, type: string) =>
        onClick(
            {
                ...item,
                name: item.name.substring(
                    item.selectedTextIndexes?.[0] ?? 0,
                    item.selectedTextIndexes?.[1] ?? item.name.length,
                ),
            },
            type,
        );

    const domainWithSubdomain = getDomainWithSubdomain(domain ?? "", subdomain ?? "");
    const siteInfo = await fetchSiteInfoQuery({
        keys: domainWithSubdomain,
        mainDomainOnly: true,
    });

    const startingIndex = query.indexOf(domain ?? "");
    const results: IWebsitePageResult[] = [
        renderItemCreator(
            "query",
            partialOnClickFunction,
        )({
            domain,
            searchType: "domain",
            name: query,
            selectedTextIndexes: [startingIndex, startingIndex + (domain?.length ?? 0)],
            secondaryText: translate("querybar.websitePages.secondaryText.domain"),
            // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — SiteInfoResponse not typed with index signature
            icon: siteInfo?.[domainWithSubdomain]?.icon,
        }),
    ];

    if (subdomain?.length) {
        const startingIndex = query.indexOf(domainWithSubdomain);
        results.push(
            renderItemCreator(
                "query",
                partialOnClickFunction,
            )({
                domain,
                searchType: "subdomain",
                name: query,
                selectedTextIndexes: [startingIndex, startingIndex + domainWithSubdomain.length],
                secondaryText: translate("querybar.websitePages.secondaryText.subdomain"),
                // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — SiteInfoResponse not typed with index signature
                icon: siteInfo?.[domainWithSubdomain]?.icon,
            }),
        );
    }

    const folderFormatted = removeTrailingSlash(folder ?? "");
    const { folder: addFolderIndicator = true } = addCustomIndicator ?? {};
    if (folderFormatted?.length && addFolderIndicator) {
        const folderName = domainWithSubdomain + folderFormatted;
        const folderStartingIndex = query.indexOf(folderName);
        results.push(
            renderItemCreator(
                "query",
                partialOnClickFunction,
            )({
                domain,
                searchType: "folder",
                name: query.substring(0, folderStartingIndex + folderName.length),
                selectedTextIndexes: [folderStartingIndex, folderStartingIndex + folderName.length],
                reverseEllipsis: true,
                secondaryText: translate("querybar.websitePages.secondaryText.page"),
                afterElementText: "/*",
                // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — SiteInfoResponse not typed with index signature
                icon: siteInfo?.[domainWithSubdomain]?.icon,
            }),
        );

        const { exact: addExactIndicator = true } = addCustomIndicator ?? {};
        if (addExactIndicator) {
            const exactUrlStartingIndex = query.indexOf(domainWithSubdomain);
            results.push(
                renderItemCreator(
                    "query",
                    partialOnClickFunction,
                )({
                    domain,
                    searchType: "exact",
                    originalQuery,
                    name: query,
                    selectedTextIndexes: [exactUrlStartingIndex, query.length],
                    reverseEllipsis: true,
                    secondaryText: translate("querybar.websitePages.secondaryText.url"),
                    // @ts-ignore FIXME: strict migration - Element implicitly has an 'any' type — SiteInfoResponse not typed with index signature
                    icon: siteInfo?.[domainWithSubdomain]?.icon,
                }),
            );
        }
    }

    const resultsFiltered = results.filter((result) => {
        // remove items that are in excludes and have the same url
        const indexes = result.selectedTextIndexes ?? [0, result.name.length];
        return !excludes.some((exclude) => exclude.url === result.name.substring(indexes[0], indexes[1]));
    });
    return [...resultsFiltered, ...extraItems];
};

export const getPageLevelAutocompleteRecents = (
    recentSearches: FixmeType[] = [],
    excludes: IChosenItem[],
    showCompareSites: boolean,
    onItemClick: (item: any, type: string) => void,
    withoutMainItemDuplicates = false,
) => {
    const hasNoRecentsData = recentSearches[0] === autocompleteStates.NO_DATA_RECENTS;
    const recents = recentSearches.reduce((agr: any[], item) => {
        if (item?.data?.type !== "website") {
            return agr;
        }

        const { domain } = getUrlComponents(item?.data?.mainItem);

        const shouldBeIncluded = excludes.every((exclude) => {
            const pageFilterString = item.data.params?.pageFilter;
            if (pageFilterString) {
                const pageFilter: IChosenItemParams = JSON.parse(pageFilterString);
                return pageFilter.every((page) => page.url !== exclude.url);
            }

            return exclude.domain !== domain;
        });

        if (withoutMainItemDuplicates) {
            const isDuplicate = agr.some(
                (recent) => recent.name === item?.data?.mainItem || recent?.mainSite?.domain == item?.data?.mainItem,
            );
            if (isDuplicate) {
                return agr;
            }
        }

        if (shouldBeIncluded) {
            const normalizedItem = normalizedPageLevelRecent(item, showCompareSites);
            agr.push(renderItemCreator("recent", onItemClick)({ ...normalizedItem, domain }));
        }
        return agr;
    }, []);

    const uniqueRecents = uniqBy(recents, (item) => JSON.stringify(item));
    return { recents: uniqueRecents, hasNoRecentsData };
};

export const handleGetListItems =
    (
        defaultSearchType: WebsitePageSearchType,
        defaultValue: string,
        getListItems: (query: string, originalQuery?: string) => Promise<any[]>,
    ) =>
    async (query: string) => {
        let normalizedQuery = query;
        const { domain, subdomain, folder, queryParams, error } = getUrlComponents(query);
        if (error) {
            return [];
        }

        if (query) {
            normalizedQuery =
                constructQueryWithNormalizedDomainAndSubdomain(domain ?? "", subdomain ?? "", folder, queryParams) ??
                query;
        }

        const listItems = await getListItems(normalizedQuery, query);

        const shouldUseDefaultSearchType = normalizedQuery === defaultValue;
        let searchType = defaultSearchType;
        if (!shouldUseDefaultSearchType) {
            if (queryParams?.length) {
                searchType = "exact";
            } else if (folder?.length) {
                searchType = "folder";
            } else if (subdomain?.length) {
                searchType = "subdomain";
            } else {
                searchType = "domain";
            }
        }

        const selectedItemIndex = listItems.findIndex((item) => item.searchType === searchType);
        if (selectedItemIndex !== -1) {
            const [selectedItem] = listItems.splice(selectedItemIndex, 1);
            listItems.unshift(selectedItem);
        }

        return listItems;
    };

export const transformSiteListToParam = (siteList: IChosenItemParams) => {
    return JSON.stringify(siteList.map(({ url, searchType }) => ({ url, searchType })));
};

export const filterOutExcludes = (excludes: IChosenItem[] | IWebsiteItem[]) => (site: ISite) =>
    !excludes.find((exclude) => exclude.url === site.name);

export const filterOutBlockList = (site: ISite) =>
    Array.isArray(site.blockStatus) ? !site.blockStatus.includes(BLOCK_SITE_STATUS) : true;

export const isValidSiteItem = (excludes: Array<IChosenItem>) => (x: ISite) =>
    !excludes.find((exclude) => exclude.name === x.name) && filterOutBlockList(x);

export const filterOutQuery = (query: string) => (site: ISite) => site.name !== query;

// Normalize query to be in the format of
// lowercase.lowercase.tld/lowercaseOrUppercase?lowercaseOrUppercase=lowercaseOrUppercase
const constructQueryWithNormalizedDomainAndSubdomain = (
    domain: string,
    subdomain: string,
    folder: string,
    queryParams: string,
) => {
    let normalizedQuery = getDomainWithSubdomain(domain, subdomain);
    if (folder?.length) {
        normalizedQuery = `${normalizedQuery}${folder}`;
    }
    if (queryParams?.length) {
        normalizedQuery = `${normalizedQuery}?${queryParams}`;
    }
    return normalizedQuery;
};
